/**
 * Ticker History Tracking Utility
 *
 * Manages recent and frequently accessed tickers across the application.
 * Stores data in localStorage for persistence across sessions.
 *
 * Features:
 * - Track ticker views with timestamp and frequency
 * - Smart sorting by recency + frequency
 * - Limit to top 15 tickers
 * - Import tickers from watchlist contracts
 * - Clear history functionality
 */

const STORAGE_KEY = 'stocktimus_ticker_history';
const MAX_TICKERS = 15;

/**
 * Get all ticker history from localStorage
 * @returns {Array} Array of ticker objects {ticker, lastAccessed, viewCount, source}
 */
export const getTickerHistory = () => {
  try {
    const history = localStorage.getItem(STORAGE_KEY);
    return history ? JSON.parse(history) : [];
  } catch (error) {
    console.error('Error reading ticker history:', error);
    return [];
  }
};

/**
 * Save ticker history to localStorage
 * @param {Array} history - Array of ticker objects
 */
const saveTickerHistory = (history) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(history));
  } catch (error) {
    console.error('Error saving ticker history:', error);
  }
};

/**
 * Track a ticker access (view)
 * @param {string} ticker - Ticker symbol (e.g., "AAPL")
 * @param {string} source - Source of access (e.g., "dashboard", "watchlist", "screener")
 */
export const trackTickerAccess = (ticker, source = 'dashboard') => {
  if (!ticker || typeof ticker !== 'string') return;

  const history = getTickerHistory();
  const existingIndex = history.findIndex(item => item.ticker === ticker);

  if (existingIndex !== -1) {
    // Update existing ticker
    history[existingIndex] = {
      ...history[existingIndex],
      lastAccessed: Date.now(),
      viewCount: history[existingIndex].viewCount + 1,
      source: source // Update to latest source
    };
  } else {
    // Add new ticker
    history.push({
      ticker: ticker.toUpperCase(),
      lastAccessed: Date.now(),
      viewCount: 1,
      source
    });
  }

  // Sort and limit to top tickers
  const sortedHistory = sortTickerHistory(history).slice(0, MAX_TICKERS);
  saveTickerHistory(sortedHistory);

  // Dispatch custom event for components to react
  window.dispatchEvent(new CustomEvent('tickerHistoryUpdated'));
};

/**
 * Bulk import tickers (e.g., from watchlist contracts)
 * @param {Array<string>} tickers - Array of ticker symbols
 * @param {string} source - Source identifier
 */
export const importTickers = (tickers, source = 'watchlist') => {
  if (!Array.isArray(tickers) || tickers.length === 0) return;

  const history = getTickerHistory();
  const now = Date.now();

  tickers.forEach(ticker => {
    const existingIndex = history.findIndex(item => item.ticker === ticker);

    if (existingIndex === -1) {
      // Only add if not already tracked (don't update existing)
      history.push({
        ticker: ticker.toUpperCase(),
        lastAccessed: now,
        viewCount: 1,
        source
      });
    }
  });

  // Sort and limit
  const sortedHistory = sortTickerHistory(history).slice(0, MAX_TICKERS);
  saveTickerHistory(sortedHistory);

  window.dispatchEvent(new CustomEvent('tickerHistoryUpdated'));
};

/**
 * Smart sorting algorithm: combines recency and frequency
 * More recent views and higher view counts get higher scores
 * @param {Array} history - Array of ticker objects
 * @returns {Array} Sorted array
 */
const sortTickerHistory = (history) => {
  const now = Date.now();
  const ONE_DAY = 24 * 60 * 60 * 1000;
  const ONE_WEEK = 7 * ONE_DAY;

  return history.sort((a, b) => {
    // Calculate recency score (0-100)
    const recencyA = Math.max(0, 100 - ((now - a.lastAccessed) / ONE_WEEK) * 100);
    const recencyB = Math.max(0, 100 - ((now - b.lastAccessed) / ONE_WEEK) * 100);

    // Calculate frequency score (logarithmic to prevent dominance)
    const frequencyA = Math.log(a.viewCount + 1) * 20;
    const frequencyB = Math.log(b.viewCount + 1) * 20;

    // Combined score: 70% recency, 30% frequency
    const scoreA = (recencyA * 0.7) + (frequencyA * 0.3);
    const scoreB = (recencyB * 0.7) + (frequencyB * 0.3);

    return scoreB - scoreA;
  });
};

/**
 * Get sorted ticker history (ready for display)
 * @returns {Array} Sorted ticker history
 */
export const getSortedTickerHistory = () => {
  const history = getTickerHistory();
  return sortTickerHistory(history);
};

/**
 * Clear all ticker history
 */
export const clearTickerHistory = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent('tickerHistoryUpdated'));
  } catch (error) {
    console.error('Error clearing ticker history:', error);
  }
};

/**
 * Remove a specific ticker from history
 * @param {string} ticker - Ticker to remove
 */
export const removeTickerFromHistory = (ticker) => {
  const history = getTickerHistory();
  const filtered = history.filter(item => item.ticker !== ticker);
  saveTickerHistory(filtered);
  window.dispatchEvent(new CustomEvent('tickerHistoryUpdated'));
};

/**
 * Format last accessed time for display
 * @param {number} timestamp - Unix timestamp in milliseconds
 * @returns {string} Formatted time string
 */
export const formatLastAccessed = (timestamp) => {
  const now = Date.now();
  const diff = now - timestamp;

  const MINUTE = 60 * 1000;
  const HOUR = 60 * MINUTE;
  const DAY = 24 * HOUR;
  const WEEK = 7 * DAY;

  if (diff < MINUTE) {
    return 'Just now';
  } else if (diff < HOUR) {
    const minutes = Math.floor(diff / MINUTE);
    return `${minutes}m ago`;
  } else if (diff < DAY) {
    const hours = Math.floor(diff / HOUR);
    return `${hours}h ago`;
  } else if (diff < WEEK) {
    const days = Math.floor(diff / DAY);
    return `${days}d ago`;
  } else {
    const weeks = Math.floor(diff / WEEK);
    return `${weeks}w ago`;
  }
};

/**
 * Get source icon for display
 * @param {string} source - Source identifier
 * @returns {string} Emoji icon
 */
export const getSourceIcon = (source) => {
  const icons = {
    dashboard: '📊',
    watchlist: '👁️',
    screener: '🔍',
    default: '📈'
  };
  return icons[source] || icons.default;
};
